<template>
  <div class='wrapper-page'>
    <contact-form ref='contactForm' @success='loadContact()' />

    <div id='contact-details' class='container-fluid'>
      <h1 class='page-title'>
        {{ contact.name }}

        <a @click.prevent='$refs.contactForm.open(contact)' href='#' class='edit'>
          {{$t('contacts.show.edit')}}
        </a>
      </h1>

      <div class='contact-info'>
        <div class="company-details">
          <h5>
            {{ $t('contacts.show.company') }}
            <strong v-if='contact.company'>
              {{ contact.company }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_company_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.position') }}
            <strong v-if='contact.position'>
              {{ contact.position }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_position_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.company_address') }}
            <strong v-if='contact.address'>
              {{ contact.address }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_company_address_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.expired_files_link') }}

            <div class="copy-to-clipboard-wrapper">
              <input type="text" name="public_link" id="public_link" :value="contactPageUrl" disabled="disabled" class="public_link input-link-to-clipboard">

              <a href="" class="btn btn-outline-primary float-right copy-link-to-clipboard" @click.prevent="copyToClipboard($event, '.input-link-to-clipboard')">
                <i class="fas fa-link"></i>
                {{ $t('folder_show.settings.upload_link_button') }}
              </a>
            </div>
          </h5>
        </div>
        <div class="contact-details">
          <h5>
            {{ $t('contacts.show.email') }}
            <strong v-if='contact.email'>
              {{ contact.email }}
            </strong>
            <strong class='red-text' v-else>
              {{ $t('contacts.show.no_email') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.language') }}
            <strong v-if="contact.locale === 'de'">
              {{ $t('contacts.edit_modal.german') }}
            </strong>
            <strong v-else>
              {{ $t('contacts.edit_modal.english') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.notes') }}
            <strong v-if='contact.notes'>
              {{ contact.notes }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_notes_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.communication_status') }}
            <router-link
              :data-tooltip='lastMessageAlt'
              :to="{name: 'contact_communications', params: { id: id }}"
              class='own-tooltip own-tooltip-right'>
              <img :src='lastMessageSrc'/>
            </router-link>
          </h5>
          <h5>
            {{ $t('contacts.show.files') }}
            <strong>{{ contactStats.records_count }}</strong>
          </h5>
        </div>

        <div class='contact-tags'>
          <h5 class='tags-dl'>
            {{ $t('contacts.show.tags') }}
          </h5>

          <div class='tags-cont'>
            <tag-manager :resource='contact' resourceType='contact'
                         v-on:reload-list='loadContact()'/>

            <div :class='tag.color' class='existing-tags-wrapper'
                 v-for='tag in contact.tags'>
              <div class='btn-group btn-group-toggle' data-toggle='buttons'>
                <label class='btn active'>
                  <router-link :to="{name: 'tag', params: { id: tag.id }}">
                    {{ tag.name }}
                  </router-link>
                </label>

                <label @click.prevent='removeConnectedTag(tag)' class='btn'>
                  <i class='fa fa-remove'></i>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="float-left checklist-status">
          <h5>
            {{ $t('contacts.show.checklist_status') }}
          </h5>
          <checklist-status :completed-items-count='contactStats.checked_checklist_items' :items-count='contactStats.total_checklist_items'/>
        </div>
      </div>

      <div class='nav-tabs-wrapper'>
        <ul role='tablist' class='nav nav-tabs'>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_folders', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.folders')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_communications', params: { id: id } }" class="nav-link show active">
              {{$t('contacts.show.communication')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_interactive_notes', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.interactive_notes')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_emails', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.automated_emails')}}
            </router-link>
          </li>
        </ul>
      </div>

      <div class='tab-content'>
        <div class="tab-pane active text-center">
          <loading v-if="loading"/>
          <div class='inbox-messages-list' v-else>
            <div class='contact-message-wrapper' v-for="inbox in orderedInbox" :key="inbox.id" v-if="inboxes.length > 0">
              <div class='id-card-wrapper'>
                <div class='img-wrapper'>
                  <user-image :user='interactionUserPhoto(inbox)' />
                </div>
                <strong>{{interactionUserName(inbox)}}</strong>
                {{formatDate(inbox.interaction.created_at)}}
              </div>
              <div class='message'>
                <div class='messages-list-item'>
                  <div class='time'>
                    {{ formatTime(inbox.interaction.created_at) }}
                  </div>

                  <div v-if="inbox.interaction_type === 'DocumentRequest'">
                    <div v-if='inbox.interaction.request_files'>
                      <div v-if='inbox.interaction.folder && inbox.interaction.shared_checklist'
                           v-html="$t('contacts.show.you_requested_files_with_checklist', { contact: contact.name, folder: interactionFolderLink(inbox.interaction.folder) })">
                      </div>
                      <div v-else-if='inbox.interaction.folder'
                           v-html="$t('contacts.show.you_requested_files', { contact: contact.name, folder: interactionFolderLink(inbox.interaction.folder) })">
                      </div>

                      <div v-if='inbox.interaction.message && inbox.interaction.message.length > 0'>
                        {{ $t('contacts.show.here_is_your_message') }}

                        <div class='interaction-message' v-html='inbox.interaction.message'></div>
                      </div>

                      <div  v-for="record in attachedRecordsByDocRequestId[inbox.interaction_id]" :key="record.id">
                        <router-link :to="{ name: 'record', params: { id: record.id, propRecords: attachedRecordsByDocRequestId[inbox.interaction_id], referrer: $route.fullPath } }">
                          {{record.filename}}
                        </router-link>
                      </div>
                    </div>

                    <div v-else>
                      <div v-if='inbox.interaction.message && inbox.interaction.message.length > 0'>
                        <div class='interaction-message' v-html='inbox.interaction.message'></div>
                        <div  v-for="record in attachedRecordsByDocRequestId[inbox.interaction_id]" :key="record.id">
                          <router-link :to="{ name: 'record', params: { id: record.id, propRecords: attachedRecordsByDocRequestId[inbox.interaction_id], referrer: $route.fullPath } }">
                            {{record.filename}}
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="inbox.interaction_type === 'UploadResponse'">
                    <div v-html="$t('contacts.show.contact_uploaded', { folder: interactionFolderLink(inbox.interaction.folder) })"></div>

                    <div v-for='record in recordsByUpResponseId[inbox.interaction_id]'>
                      <router-link :to="{ name: 'record', params: { id: record.id, propRecords: recordsByUpResponseId[inbox.interaction_id], referrer: $route.fullPath } }">
                        {{ record.filename }}
                      </router-link>
                    </div>
                  </div>

                  <div v-else-if="inbox.interaction_type === 'VersionResponse' && versionResponseById[inbox.interaction_id]">

                    <div v-if="versionResponseById[inbox.interaction_id].reply === 'nofile'">
                      <div v-html="$t('contacts.show.contact_replaced_nofile', { record: interactionRecordLink(versionResponseById[inbox.interaction_id].old_record), contact: contact.name })"></div>
                    </div>
                    <div v-else-if="versionResponseById[inbox.interaction_id].reply === 'stillvalid'">
                      <div v-html="$t('contacts.show.contact_replaced_stillvalid', { record: interactionRecordLink(versionResponseById[inbox.interaction_id].old_record), contact: contact.name })"></div>
                    </div>
                    <div v-else>
                      <div v-html="$t('contacts.show.contact_replaced', { record: versionResponseById[inbox.interaction_id].old_record.filename, contact: contact.name, folder: interactionFolderLink(inbox.interaction.folder) })"></div>

                      <router-link :to="{ name: 'record', params: { id: versionResponseById[inbox.interaction_id].new_record.id, propRecords: [versionResponseById[inbox.interaction_id].new_record.id], referrer: $route.fullPath } }">
                        {{ versionResponseById[inbox.interaction_id].new_record.filename }}
                      </router-link>
                    </div>

                  </div>

                  <div v-else-if="inbox.interaction_type === 'VersionRequest'">
                    <div v-html="$t('contacts.show.you_requested_new_version', { record: interactionRecordLink(recordByVersionRequestId[inbox.interaction_id]), contact: contact.name })"></div>

                    <div v-if='inbox.interaction.message && inbox.interaction.message.length > 0'>
                      {{ $t('contacts.show.here_is_your_message') }}

                      <strong>
                        <div v-html='inbox.interaction.message'></div>
                      </strong>
                    </div>
                  </div>

                  <div v-else>
                    <div v-if='inbox.interaction.folder'
                         v-html="$t('contacts.show.contact_responded', { contact: contact.name, folder: interactionFolderLink(inbox.interaction.folder) })">
                    </div>

                    <div v-if='inbox.interaction.message && inbox.interaction.message.length > 0'>
                      {{ $t('contacts.show.here_is_their_message') }}

                      <strong>
                        <div v-html='inbox.interaction.message'></div>
                      </strong>
                    </div>

                    <div v-for='record in recordsByDocResponseId[inbox.interaction_id]'>
                      <router-link :to="{ name: 'record', params: { id: record.id, propRecords: recordsByDocResponseId[inbox.interaction_id], referrer: $route.fullPath } }">
                        {{ record.filename }}
                      </router-link>
                    </div>
                  </div>
                </div>
                <p class='status'>
                  {{inbox.interaction.status}}
                </p>
              </div>

            </div>
            <div class="empty-list-wrapper" v-if="inboxes.length === 0">
              <p>{{$t('contacts.show.no_conversation')}}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import _modal from '../components/_modal'
  import _loading from '../components/_loading'
  import _user_image from '../components/_user_image'
  import tag_manager from '../components/tags/_tags_manager'
  import checklistStatus from '../components/contacts/_checklist_status'
  import _contact_form from '../components/shared/contacts/_contact_form'

  export default {
    components: {
      modal: _modal,
      'user-image': _user_image,
      'tag-manager': tag_manager,
      'contact-form': _contact_form,
      'checklist-status': checklistStatus,
      'loading': _loading,
    },
    props: ['id'],
    created() {
      this.loadContact()
      this.loadResponses()
      this.store.sendMetrics({name: 'contact_communication', params: this.$route.params, path: this.$route.fullPath})
    },
    watch: {
      id() {
        this.loadContact()
        this.loadResponses()
      },
    },
    data() {
      return {
        modalContact: {},
        loading: true
      }
    },
    computed: {
      contacts() {
        return this.lists.contacts || []
      },
      contactStats () {
        if (!this.stats.contacts)
          return {}

        return this.stats.contacts[0]
      },
      folderStats() {
        if (!this.stats.folders)
          return {}

        const stats = {}
        this.stats.folders.forEach(f => stats[f.id] = f)
        return stats
      },
      recordsCount() {
        return Object.values(this.folderStats).reduce((s, f) => s + f.records_count, 0)
      },
      checklistItems() {
        return Object.values(this.folderStats).reduce((s, f) => s + f.total_checklist_items, 0)
      },
      checkedItems() {
        return Object.values(this.folderStats).reduce((s, f) => s + f.checked_checklist_items, 0)
      },
      contact() {
        return this.state.contact || {}
      },
      inboxes() {
        return this.contact.inboxes || []
      },
      lastMessage() {
        const documentRequests = this.inboxes.filter(i => i && i.interaction_type === 'DocumentRequest').map(i => i.interaction)

        return documentRequests[documentRequests.length - 1]
      },
      lastMessageSrc() {
        if (!this.lastMessage)
          return require('../assets/gray-env.png')

        switch (this.lastMessage.status) {
          case 'pending':
            return require('../assets/yellow-env.png')
          case 'failed':
            return require('../assets/red-env.png')
          case 'delivered':
          case 'opened':
            return require('../assets/green-env.png')
        }
      },
      lastMessageAlt() {
        if (!this.lastMessage)
          return this.$t('contacts.no_sent_message')

        const d = this.formatDate(this.lastMessage.created_at)
        const t = new Date(this.lastMessage.created_at).toLocaleTimeString(this.locale, { timeStyle: 'short' })
        switch (this.lastMessage.status) {
          case 'pending':
            return this.$t('contacts.sending_message', { date: d, time: t })
          case 'failed':
            return this.$t('contacts.message_not_sent', { date: d, time: t })
          case 'delivered':
          case 'opened':
            return this.$t('contacts.message_sent', { date: d, time: t })
        }
      },
      messages_org(){
        return this.state.messages_org || []
      },
      orderedInbox() {
        return this.inboxes.slice(0).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      },
      documentResponses() {
        return this.state.documentResponses || []
      },
      uploadResponses() {
        return this.state.uploadResponses || []
      },
      versionRequests() {
        return this.state.versionRequests || []
      },
      versionResponses() {
        return this.state.versionResponses || []
      },
      documentRequests(){
        return this.state.documentRequests || []
      },
      recordsByDocResponseId() {
        return this.documentResponses.reduce((acc, item) => Object.assign(acc, { [item.id]: item.records }), {})
      },
      recordByVersionRequestId() {
        return this.versionRequests.reduce((acc, item) => Object.assign(acc, { [item.id]: item.old_record }), {})
      },
      recordsByUpResponseId() {
        return this.uploadResponses.reduce((acc, item) => Object.assign(acc, { [item.id]: item.records }), {})
      },
      versionResponseById() {
        return this.versionResponses.reduce((acc, item) => Object.assign(acc, { [item.id]: item }), {})
      },
      attachedRecordsByDocRequestId(){
        return this.documentRequests.reduce((acc, item) => Object.assign(acc, { [item.id]: item.attached_records }), {})
      },
      contactPageUrl() {
        return window.location.origin + this.$router.resolve({ name: 'public_contact_page', params: { uuid: this.contact.uuid } }).href
      },
    },
    methods: {
      loadContact() {
        this.store.getContact(this.$props.id).then(() =>  this.loading = false)
        this.statStore.getContacts({ 'filters[id]': this.$props.id })
      },
      loadResponses() {
        this.store.getOrgMessages(this.$props.id)
        this.store.getDocumentResponses(this.$props.id)
        this.store.getUploadResponses(this.$props.id)
        this.store.getVersionResponses(this.$props.id)
        this.store.getVersionRequests(this.$props.id)
        this.store.getDocumentRequests(this.$props.id)
      },
      updateContact(event) {
        this.store.updateContact(this.contact.id, new FormData(event.target)).then(() => {
          this.loadContact()
          this.$refs.contactForm.close()
        })
      },
      removeConnectedTag(tag) {
        this.store.removeConnectedTag(tag.id, { contact_id: this.contact.id }).then(() => {
          this.store.sendMetrics({name: 'remove_tag_from_contact', params: this.$route.params, path: this.$route.fullPath})

          this.loadContact()
        })
      },
      interactionUserName(inbox) {
        if (inbox.interaction_type === 'DocumentRequest' || inbox.interaction_type === 'VersionRequest') {
          return this.getMsgCreatorFullName(inbox)
        } else {
          return inbox.contact.name
        }
      },
      interactionUserPhoto(inbox){
        if (inbox.interaction_type === 'DocumentRequest' || inbox.interaction_type === 'VersionRequest') {
          return this.getMsgCreator(inbox)
        } else {
          return { full_name: inbox.contact.name }
        }
      },
      getMsgCreator(inbox){
        const found = this.messages_org.find(m => m.interaction_id === inbox.interaction_id)
        return found ? found.interaction.creator : {}
      },
      getMsgCreatorFullName(inbox){
        let creator = this.getMsgCreator(inbox)
        return creator.full_name
      },
      interactionFolderLink(folder) {
        const link = this.$router.resolve({ name: 'folder', params: { id: folder.id } }).href

        return `
            <a href="${link}">
              ${folder.name}
            </a>
        `
      },
      interactionRecordLink(record) {
        const link = this.$router.resolve({ name: 'record', params: { id: record.id } }).href

        return `
            <a href="${link}">
              ${record.filename}
            </a>
        `
      },
    }
  }
</script>

<style lang='sass'>
  @import '../styles/checklists'
  @import '../styles/contact'
</style>
